import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import {
  Radio,
  RadioGroupMinimal,
} from '@lifeomic/chroma-react/components/Radio';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import {
  AlertTriangle,
  CheckList,
  HeartCircle,
  Info,
} from '@lifeomic/chromicons';
import clsx from 'clsx';
import _startCase from 'lodash/startCase';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { usePDF } from 'react-to-pdf';
import {
  useActiveUserId,
  useDisableAnimations,
  useNotify,
  usePolicy,
} from '../../../../hooks';
import { useSendAgentFeedback } from '../../../../hooks/useSendAgentFeedback';
import maskedModeActions from '../../../../redux/modules/maskedMode/actions';
import { SYSTEM_ACTION_PLAN_LAST_EDITED_BY } from '../../../../util/fhir';
import EmptyPageState from '../../../EmptyPageState';
import EnforceABAC, { permissions } from '../../../EnforceABAC';
import { DashboardInjectedProps } from '../../util/types';
import { ActionItems } from '../ActionPlan/ActionItems';
import { AttachmentArbiterProvider } from '../AITools/AttachmentArbiter';
import { ViewMode } from '../AITools/Attachments/InlineAttachment';
import { ImpressionEditor } from '../AITools/ImpressionEditor';
import {
  ImpressionItem,
  KnownImpressionTypes,
} from '../AITools/ImpressionEditor/ImpressionItem';
import { CombinedRenderingTrackingProvider } from '../AITools/RenderingTracking/CombinedRenderingTrackingProvider';
import { usePresentationKeyboardShortcuts } from '../AITools/ImpressionEditor/useKeyboardShortcuts';
import LabResults from '../LabResults';
import {
  ExecutiveSummaryProvider,
  useExecutiveSummaryData,
  useExecutiveSummarySelection,
  useExecutiveSummaryUI,
  useGenerateDrawer,
} from './Context';
import { useImpressionEditorContext } from '../AITools/ImpressionEditor/Context';
import { AnnotationPlugin } from '../AITools/ImpressionEditor/MarkdownEditor/plugins/AnnotationPlugin';
import { SuggestionsDrawer } from '../SuggestionsDrawer';
import { ControlBar } from './ControlBar';
import { CoverPage } from './CoverPage';
import {
  GenerateMenuAction,
  GenerateMenuFormBody,
  GenerateMenuModal,
  useGenerateMenuForm,
} from './GenerateMenuModal/GenerateMenuModal';
import { messages } from './messages';
import { Metadata } from './Metadata';
import { Presentation } from './Presentation';
import { OlderAnnualReportBanner } from './OlderAnnualReportBanner';
import { ExecutiveSummaryData } from './executive-summary';
import { StatusValue } from './StatusSelector';
import { useExecutiveSummaryGeneration } from './useExecutiveSummaryGeneration';
import { findLatestActionPlan, findNewerActiveAnnualReport } from './utils';
import { KnownExtraSections } from '../../../AITemplates/ImpressionTypeFilter';
import {
  PHC_DOCUMENT_SURFACE_CLASS,
  useDocumentSurfaceStyles,
} from './documentSurface';

type ExecutiveSummary = { impressions: fhir.ClinicalImpression[] };

export const useBrandedDocumentStyles = makeStyles<{
  isEditing: boolean;
  viewMode: ViewMode;
}>((theme: Theme) => ({
  presentationContent: ({ viewMode }) => ({
    borderRadius: viewMode === 'print' ? 0 : theme.pxToRem(10),
    minHeight: 0,
    overflow: 'hidden',
    zIndex: 10,
    animation: viewMode === 'print' ? 'none' : '$fadeIn 0.3s ease-out',
    ...(viewMode === 'print' && {
      width: '100%',
      padding: theme.spacing(4),
      backgroundColor: '#105dac',
      backgroundImage: `linear-gradient(
          to right,
          #112a70,
          #08a5d2 65%
        )`,
    }),
  }),
}));

const useStyles = makeStyles<{
  isEditing: boolean;
  viewMode: ViewMode;
  disableAnimations: boolean;
}>((theme: Theme) => ({
  root: {
    background: theme.palette.background.default,
    maxHeight: '100%',
    height: '100%',
  },
  content: {
    height: '100%',
    position: 'relative',
  },

  sideBar: ({ disableAnimations }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    height: '100%',
    maxWidth: theme.pxToRem(310),
    minWidth: theme.pxToRem(310),
    overflowY: 'auto',
    opacity: 1,
    transform: 'translateX(0)',
    // Simplify transitions when animations are disabled to prevent performance issues
    transition: disableAnimations
      ? 'none'
      : 'transform 0.25s ease-out, opacity 0.2s ease-out',
    // Add will-change when animations are enabled to hint at animation
    willChange: disableAnimations ? 'auto' : 'transform, opacity',
  }),
  sideBarHidden: ({ disableAnimations }) => ({
    transform: 'translateX(-100%)',
    // Simplify transitions when animations are disabled
    transition: disableAnimations
      ? 'none'
      : 'transform 0.25s ease-out, opacity 0.2s ease-out',
    borderRightColor: 'transparent',
    opacity: 0,
    position: 'absolute',
    zIndex: 1,
  }),
  body: (props: { viewMode: ViewMode; isEditing: boolean }) => ({
    flex: 1,
    flexDirection: 'column',
    maxHeight: '100%',
    maxWidth: '100%',
    overflow: 'auto',
    position: 'relative',
    ...(props.viewMode === 'presentation-expanded' ||
    props.viewMode === 'presentation-compact' ||
    props.viewMode === 'print'
      ? {
          backgroundColor: 'transparent',
          '&&': {
            padding: 0,
          },
        }
      : {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.default
              : theme.palette.darkGraphite[50],
          padding: theme.spacing(2),
        }),
    ...(props.isEditing
      ? {
          paddingBottom: theme.pxToRem(84),
        }
      : { padding: theme.spacing(2) }),
  }),
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  mainContent: ({ viewMode, disableAnimations }) => ({
    // Disable transitions when animations are disabled or when printing
    transition:
      viewMode === 'print' || disableAnimations
        ? 'none'
        : 'left 0.25s ease-out, margin-right 0.25s ease-out',
    position: 'relative',
    left: theme.pxToRem(310),
    marginLeft: theme.pxToRem(-310),
    marginRight: theme.pxToRem(310),
    flex: 1,
    // Add will-change when animations are enabled to hint at animation
    willChange:
      disableAnimations || viewMode === 'print' ? 'auto' : 'left, margin',
    '&.sidebarHidden': {
      marginLeft: 0,
      marginRight: 0,
      left: 0,
    },
  }),

  whiteContainer: {
    backgroundColor: theme.palette.common.white,
  },
  actionPlanVersionControls: {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : theme.palette.common.white,
    flexShrink: 0,
    padding: theme.spacing(2, 2, 0),
  },
  actionPlanVersionAlert: {
    marginTop: theme.spacing(1),
  },
}));

type ExecutiveSummaryProps = DashboardInjectedProps & {
  enabledSummaries?: KnownImpressionTypes[];
  disableControls?: boolean;
  selectedReportId?: string;
  onReportChange?: (reportId: string) => void;
  selectedSection?: KnownImpressionTypes | KnownExtraSections;
  onSectionChange?: (
    section: KnownImpressionTypes | KnownExtraSections,
  ) => void;
  renderKnowledgeBaseCitations?: boolean;
};

type ActionPlanVersion = 'this' | 'latest';

const ActionPlanVersionControls: React.FC<{
  value: ActionPlanVersion;
  onChange: (value: ActionPlanVersion) => void;
  className?: string;
  alertClassName?: string;
}> = ({ value, onChange, className, alertClassName }) => {
  const { formatMessage } = useIntl();
  const isViewingLatest = value === 'latest';

  return (
    <Box className={className} direction="column" fullWidth>
      <RadioGroupMinimal
        aria-label={formatMessage(messages.actionPlanVersionToggle)}
        onChange={e => onChange(e.target.value as ActionPlanVersion)}
        value={value}
      >
        <Radio label={formatMessage(messages.thisActionPlan)} value="this" />
        <Radio
          label={formatMessage(messages.latestActionPlan)}
          value="latest"
        />
      </RadioGroupMinimal>
      <Alert
        className={alertClassName}
        fullWidth
        statusType={isViewingLatest ? 'info' : 'warning'}
      >
        <AlertIcon icon={isViewingLatest ? Info : AlertTriangle} />
        <AlertBody>
          {formatMessage(
            isViewingLatest
              ? messages.viewingLatestActionPlan
              : messages.viewingThisActionPlan,
          )}
        </AlertBody>
      </Alert>
    </Box>
  );
};

const ESAnnotationPlugin: React.FC = () => {
  const { annotations, setAnnotations, setIsDrawerOpen, editorRef } =
    useGenerateDrawer();
  const { isEditing } = useExecutiveSummaryUI();
  const { activeImpression } = useExecutiveSummarySelection();
  const isDraft = !!activeImpression && activeImpression.status !== 'completed';
  const hasDiffDocument = !!activeImpression?.reviewDiffDocument;

  if (!isDraft || hasDiffDocument) {
    return null;
  }

  return (
    <AnnotationPlugin
      annotations={annotations}
      onAnnotationsChange={setAnnotations}
      onRequestDrawerOpen={() => setIsDrawerOpen(true)}
      isEditing={isEditing}
      canAnnotate
      editorRef={editorRef}
    />
  );
};

const ESDrawer: React.FC<{
  patientId: string;
  executiveSummary?: ExecutiveSummaryData;
}> = ({ patientId, executiveSummary }) => {
  const { formatMessage } = useIntl();
  const {
    isDrawerOpen,
    setIsDrawerOpen,
    annotations,
    setAnnotations,
    editorRef,
    onImpressionCleanupRef,
    setIsSuggesting,
  } = useGenerateDrawer();
  const {
    activeImpression,
    setIsEditing: setEditorIsEditing,
    setIsSuggesting: setEditorIsSuggesting,
  } = useImpressionEditorContext();
  const { setIsEditing } = useExecutiveSummaryUI();
  const { refetchImpression } = useExecutiveSummaryData();

  const handleSuggestingChange = useCallback(
    (value: boolean) => {
      setIsSuggesting(value);
      setEditorIsSuggesting(value);
    },
    [setIsSuggesting, setEditorIsSuggesting],
  );

  const onClose = () => setIsDrawerOpen(false);

  const currentImpressionIdRef = React.useRef(activeImpression?.id);
  currentImpressionIdRef.current = activeImpression?.id;

  const invokedForIdRef = React.useRef<string | undefined>(
    activeImpression?.id,
  );

  const staleGuardedRefresh = useCallback(() => {
    const impressionId = invokedForIdRef.current;
    if (impressionId && impressionId === currentImpressionIdRef.current) {
      setIsEditing(true);
      setEditorIsEditing(true);
      refetchImpression(impressionId);
    }
  }, [refetchImpression, setIsEditing, setEditorIsEditing]);

  useEffect(() => {
    onImpressionCleanupRef.current = () => {
      handleSuggestingChange(false);
    };
    return () => {
      onImpressionCleanupRef.current = null;
    };
  }, [onImpressionCleanupRef, handleSuggestingChange]);

  const form = useGenerateMenuForm({
    onDismiss: onClose,
    patientId,
    executiveSummary,
  });

  const showSuggestionsTab = activeImpression?.status !== 'completed';

  return (
    <SuggestionsDrawer
      isOpen={isDrawerOpen}
      onClose={onClose}
      title={formatMessage(messages.generate)}
      impressionId={activeImpression?.id ?? ''}
      patientId={patientId}
      summary={activeImpression?.summary ?? ''}
      annotations={annotations}
      onAnnotationsChange={setAnnotations}
      editorRef={editorRef}
      onSuccess={staleGuardedRefresh}
      onFailureMessage={formatMessage(messages.generateFailure)}
      onSuggestingChange={handleSuggestingChange}
      onBeforeInvoke={() => {
        invokedForIdRef.current = currentImpressionIdRef.current;
      }}
      regenerateTab={<GenerateMenuFormBody form={form} />}
      regenerateAction={<GenerateMenuAction form={form} />}
      showSuggestionsTab={showSuggestionsTab}
      rationale={activeImpression?.suggestionRationale}
      hasPendingDiff={!!activeImpression?.reviewDiffDocument}
    />
  );
};

const _ExecutiveSummary = (props: ExecutiveSummaryProps) => {
  const { formatMessage } = useIntl();
  const disableAnimations = useDisableAnimations();

  const [notify] = useNotify();

  // Split context usage for better performance
  const {
    isLoading,
    deleteImpression,
    isSaving,
    fetchCarePlan,
    executiveSummary,
    enabledSummaries,
    effectiveSummaries,
    carePlans,
    onChangeCarePlan,
    setStatus: setExecutiveSummaryStatus,
    setDuration: setExecutiveSummaryDuration,
    setKeyFindings,
    enabledImpressionItems,
    refetchImpression,
  } = useExecutiveSummaryData();

  const { task, isGenerating } = useExecutiveSummaryGeneration({
    patientId: props.patient.id,
    onGenerationSuccess: () => {
      notify(formatMessage(messages.generateSuccess), 'success');
      fetchCarePlan();
    },
    onGenerationFailure: () =>
      notify(formatMessage(messages.generateFailure), 'error'),
    onGenerationConflict: () =>
      notify(formatMessage(messages.generateConflict), 'warning'),
  });

  const {
    impressionTypeFilter,
    setImpressionTypeFilter,
    activeImpression,
    setActiveImpression,
    viewMode,
    setViewMode,
  } = useExecutiveSummarySelection();

  const {
    isSidebarVisible,
    isPresentationModalOpen,
    setIsPresentationModalOpen,
    showFeedbackModal,
    setShowFeedbackModal,
    isEditing,
    setIsEditing,
  } = useExecutiveSummaryUI();

  const { sendAgentFeedback } = useSendAgentFeedback();
  const policy = usePolicy();

  const classes = useStyles({ viewMode, isEditing, disableAnimations });
  const documentSurfaceClasses = useDocumentSurfaceStyles();
  const docClasses = useBrandedDocumentStyles({ viewMode, isEditing });

  const hasContent = executiveSummary?.hasContent;

  const newerActiveAnnualReport = useMemo(
    () =>
      executiveSummary?.carePlan
        ? findNewerActiveAnnualReport({
            currentCarePlan: executiveSummary.carePlan,
            carePlans,
          })
        : undefined,
    [carePlans, executiveSummary?.carePlan],
  );

  const latestActionPlan = useMemo(
    () => findLatestActionPlan(carePlans),
    [carePlans],
  );
  const hasDifferentLatestActionPlan = Boolean(
    latestActionPlan?.id &&
      executiveSummary?.carePlan.id &&
      latestActionPlan.id !== executiveSummary.carePlan.id,
  );
  const [selectedActionPlanVersion, setSelectedActionPlanVersion] =
    useState<ActionPlanVersion>('latest');
  const effectiveActionPlanVersion = hasDifferentLatestActionPlan
    ? selectedActionPlanVersion
    : 'this';
  const displayedActionPlanCarePlan =
    effectiveActionPlanVersion === 'latest'
      ? latestActionPlan
      : executiveSummary?.carePlan;

  useEffect(() => {
    setSelectedActionPlanVersion('latest');
  }, [executiveSummary?.carePlan.id]);

  const traceId = executiveSummary?.traceId;

  const [comment, setComment] = useState<string>('');

  const userId = useActiveUserId();

  const [isDeleting, setIsDeleting] = useState<ImpressionItem | undefined>();

  const [isGenerationMenuOpen, setIsGenerationMenuOpen] = useState(false);
  const shouldUseDrawer = true;
  const renderKnowledgeBaseCitations =
    props.renderKnowledgeBaseCitations ?? true;

  // Include impression and care-plan IDs so the tracker resets when the user
  // switches to a different impression or annual report, even if viewMode and
  // isEditing happen to be unchanged.
  const renderContext = `${viewMode}|${impressionTypeFilter}|${isEditing}|${
    activeImpression?.id ?? ''
  }|${executiveSummary?.carePlan.id ?? ''}`;

  React.useEffect(() => {
    if (activeImpression?.impressionType) {
      setImpressionTypeFilter(activeImpression.impressionType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeImpression?.id]);

  const confirmDelete = async () => {
    if (!activeImpression?.impression.id) return;

    const ok = await deleteImpression(activeImpression.impression.id);
    if (!ok) {
      return;
    }
    setIsDeleting(undefined);
    setIsEditing(false);
    const nextImpression = enabledImpressionItems?.[0];
    setActiveImpression(nextImpression);
    setImpressionTypeFilter(nextImpression?.impressionType || 'actionItems');
  };

  const onDelete = () => {
    setIsDeleting(activeImpression);
  };

  const setStatus = (status: StatusValue) => setExecutiveSummaryStatus(status);

  const setDuration = (dates: { start?: Date; end?: Date }) => {
    setExecutiveSummaryDuration(dates.start, dates.end);
  };

  const onFindingsChange = (findings: string[]) => {
    setKeyFindings(findings);
  };

  const reduxDispatch = useDispatch();
  usePresentationKeyboardShortcuts(
    useCallback(
      (mode: ViewMode) => {
        setViewMode(mode);
        reduxDispatch(maskedModeActions.setMaskedMode(false));
        setIsPresentationModalOpen(true);
      },
      [reduxDispatch, setViewMode, setIsPresentationModalOpen],
    ),
  );

  const patientName = `${props.patient.name?.[0]?.given?.join(' ')} ${
    props.patient.name?.[0]?.family ?? ''
  }`;
  const { toPDF, targetRef } = usePDF({
    filename: `${patientName}-summary`,
    page: {
      margin: 0, // we push colors to the edges of the page and have margins built into the printed content
    },
  });

  const [isSendingFeedback, setIsSendingFeedback] = useState(false);

  const sendFeedback = async () => {
    if (!traceId || !comment) {
      return;
    }

    setIsSendingFeedback(true);

    await sendAgentFeedback({
      agentName: 'health-summary-agent',
      traceId,
      feedback: `${userId} regarding ${impressionTypeFilter}: ${comment}`, // add context to the feedback
    }).catch(e => {
      setIsSendingFeedback(false);
      notify(formatMessage(e.message), 'error');
    });

    setComment('');
    setShowFeedbackModal(false);

    setIsSendingFeedback(false);
  };

  useEffect(() => {
    if (!executiveSummary || !enabledImpressionItems?.length) return;

    const hasSelection =
      impressionTypeFilter === 'actionItems' ||
      impressionTypeFilter === 'labResults' ||
      enabledImpressionItems?.some(
        impression => impression.impressionType === impressionTypeFilter,
      );
    if (!hasSelection) {
      setActiveImpression(enabledImpressionItems?.[0]);
      setImpressionTypeFilter(
        enabledImpressionItems?.[0]?.impressionType ?? 'actionItems',
      );
    } else if (
      hasSelection &&
      impressionTypeFilter !== 'actionItems' &&
      impressionTypeFilter !== 'labResults'
    ) {
      const activeImpression = enabledImpressionItems?.find(
        impression => impression.impressionType === impressionTypeFilter,
      );
      if (activeImpression) {
        setActiveImpression(activeImpression);
      }
    }
  }, [
    executiveSummary,
    enabledImpressionItems,
    impressionTypeFilter,
    setImpressionTypeFilter,
    setActiveImpression,
  ]);

  const printableView = useMemo(() => {
    if (!executiveSummary) {
      return null;
    }
    const summaries = effectiveSummaries.map(summary => {
      const impression = executiveSummary.impressions.find(
        impression => impression.impressionType === summary,
      );
      if (!impression) {
        return null;
      }
      return (
        <ImpressionEditor.Component
          key={impression.id}
          patient={props.patient}
          impression={impression}
          viewMode={viewMode}
          renderKnowledgeBaseCitations={renderKnowledgeBaseCitations}
        />
      );
    });
    return (
      <>
        <CoverPage
          patient={props.patient}
          executiveSummary={executiveSummary}
          impressionType={undefined}
          viewMode={viewMode}
        />
        {summaries}
      </>
    );
  }, [
    effectiveSummaries,
    executiveSummary,
    props.patient,
    renderKnowledgeBaseCitations,
    viewMode,
  ]);

  if ((isLoading || isGenerating) && !hasContent) {
    return (
      <Box
        fullHeight
        fullWidth
        align="center"
        justify="center"
        gap={2}
        direction="column"
      >
        <DotLoader />
        {isGenerating && (
          <span>{formatMessage(messages.generateInProgressLong)}</span>
        )}
      </Box>
    );
  }

  if (!hasContent) {
    return (
      <Box
        direction="column"
        fullHeight
        fullWidth
        align="center"
        justify="center"
        gap={4}
      >
        <EmptyPageState
          illustration={HeartCircle}
          message={
            task
              ? task.status === 'scheduled'
                ? formatMessage(messages.scheduledButtonTooltip, {
                    scheduled: new Date(task.scheduled!).toLocaleString(),
                  })
                : task.status === 'completed'
                ? formatMessage(messages.completed)
                : task.status === 'failed'
                ? formatMessage(messages.generateFailure)
                : formatMessage(messages[task.status as keyof typeof messages])
              : formatMessage(messages.noSummaryAvailable)
          }
        />
        <EnforceABAC key="generate-now" permission={permissions.updateData}>
          <Button
            variant="outlined"
            onClick={() => setIsGenerationMenuOpen(true)}
          >
            {formatMessage(messages.generate)}
          </Button>
          <GenerateMenuModal
            isOpen={isGenerationMenuOpen}
            onDismiss={() => setIsGenerationMenuOpen(false)}
            patientId={props.patient.id}
            executiveSummary={executiveSummary ?? undefined}
          />
        </EnforceABAC>
      </Box>
    );
  }

  return (
    // We need all the annual report's attachments so the attachments search feature
    // within the presentation modal can find them.
    <AttachmentArbiterProvider
      patient={props.patient}
      attachments={executiveSummary.attachments}
    >
      <CombinedRenderingTrackingProvider context={renderContext}>
        <Presentation
          isOpen={isPresentationModalOpen}
          onDismiss={() => {
            setIsPresentationModalOpen(false);
            setViewMode('expanded');
          }}
          patient={props.patient}
          toPDF={toPDF}
          viewMode={viewMode}
        >
          <Box
            direction="column"
            fullWidth
            className={docClasses.presentationContent}
            ref={targetRef}
          >
            <Box className={classes.body} gap={1.5}>
              {impressionTypeFilter === 'labResults' ? (
                <Box
                  fullWidth
                  fullHeight
                  className={clsx(
                    classes.whiteContainer,
                    documentSurfaceClasses.root,
                    PHC_DOCUMENT_SURFACE_CLASS,
                  )}
                >
                  <LabResults patient={props.patient} />
                </Box>
              ) : impressionTypeFilter === 'actionItems' ? (
                <>
                  {hasDifferentLatestActionPlan && viewMode !== 'print' ? (
                    <ActionPlanVersionControls
                      value={effectiveActionPlanVersion}
                      onChange={setSelectedActionPlanVersion}
                      className={classes.actionPlanVersionControls}
                      alertClassName={classes.actionPlanVersionAlert}
                    />
                  ) : null}
                  <ActionItems
                    forcedViewMode={'presentation-compact'}
                    carePlan={displayedActionPlanCarePlan}
                    // in presentation mode, action items are not editable
                    removeActivity={() => void 0}
                    addActionItem={() => void 0}
                    updateActionItem={() => void 0}
                    toggleActionItemStatus={() => void 0}
                  />
                </>
              ) : (
                !!activeImpression &&
                isPresentationModalOpen &&
                (viewMode === 'presentation-expanded' ||
                viewMode === 'presentation-compact' ? (
                  <ImpressionEditor.Component
                    patient={props.patient}
                    impression={activeImpression}
                    viewMode={viewMode}
                    renderKnowledgeBaseCitations={renderKnowledgeBaseCitations}
                  />
                ) : (
                  printableView
                ))
              )}
            </Box>
          </Box>
        </Presentation>

        <Modal
          isOpen={!!isDeleting}
          onDismiss={() => setIsDeleting(undefined)}
          title={formatMessage(messages.areYouSure)}
          size={1}
          fullWidth
          actions={
            <>
              <Button variant="text" onClick={() => setIsDeleting(undefined)}>
                {formatMessage(messages.cancel)}
              </Button>
              <Button onClick={confirmDelete}>
                {formatMessage(messages.confirm)}
              </Button>
            </>
          }
        >
          {formatMessage(messages.deleteImpression, {
            impressionType: _startCase(activeImpression?.impressionType),
          })}
        </Modal>
        <Modal
          isOpen={showFeedbackModal}
          onDismiss={() => setShowFeedbackModal(false)}
          title={formatMessage(messages.leaveFeedback)}
          size={1}
          fullWidth
          actions={
            <>
              <Button
                variant="outlined"
                onClick={() => sendFeedback()}
                disabled={!comment || isSendingFeedback}
              >
                {formatMessage(messages.leaveFeedback)}
              </Button>
            </>
          }
        >
          <TextArea
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={showFeedbackModal}
            aria-label="Feedback"
            placeholder={formatMessage(messages.placeholderFeedback)}
            onChange={content => setComment(content.target.value)}
            fullWidth
            rows={10}
          />
        </Modal>
        {!isPresentationModalOpen && (
          <Box className={classes.root} direction="column">
            <Box className={classes.content}>
              <Box
                direction="column"
                className={clsx(
                  classes.sideBar,
                  (!isSidebarVisible || props.disableControls) &&
                    classes.sideBarHidden,
                )}
              >
                <Metadata
                  isSaving={isSaving}
                  isEditing={isEditing}
                  isLoading={isLoading}
                  executiveSummary={executiveSummary}
                  onStatusChange={setStatus}
                  onDurationChange={setDuration}
                  onFindingsChange={onFindingsChange}
                  carePlans={carePlans}
                  onChangeCarePlan={onChangeCarePlan}
                  policy={policy}
                />
              </Box>

              <Box
                className={clsx(
                  classes.mainContent,
                  !isSidebarVisible && 'sidebarHidden',
                )}
                direction="column"
                fullWidth
              >
                {newerActiveAnnualReport?.id ? (
                  <Box fullWidth style={{ flexShrink: 0 }}>
                    <OlderAnnualReportBanner
                      newerActiveCarePlanId={newerActiveAnnualReport.id}
                      onSwitchToLatest={onChangeCarePlan}
                    />
                  </Box>
                ) : null}
                <ControlBar
                  patientId={props.patient.id}
                  disableControls={!!props.disableControls}
                  enabledSummaries={enabledSummaries}
                />
                <Box
                  style={{
                    display: 'flex',
                    flex: 1,
                    overflow: 'hidden',
                  }}
                >
                  {impressionTypeFilter === 'labResults' ? (
                    <Box className={classes.body} gap={2} style={{ flex: 1 }}>
                      <LabResults patient={props.patient} />
                    </Box>
                  ) : impressionTypeFilter === 'actionItems' ? (
                    <Box className={classes.body} gap={2} style={{ flex: 1 }}>
                      <Box
                        className={clsx(
                          classes.whiteContainer,
                          documentSurfaceClasses.root,
                          PHC_DOCUMENT_SURFACE_CLASS,
                        )}
                        direction="column"
                        fullWidth
                        fullHeight
                        padding={5}
                      >
                        <EmptyPageState
                          message={formatMessage(messages.actionPlanMoved)}
                          illustration={CheckList}
                        />
                      </Box>
                    </Box>
                  ) : activeImpression ? (
                    <ImpressionEditor.Providers
                      patient={props.patient}
                      impression={activeImpression}
                      viewMode={viewMode}
                      refresh={fetchCarePlan}
                      refetchImpression={refetchImpression}
                      isEditing={isEditing}
                      setIsEditing={setIsEditing}
                      renderKnowledgeBaseCitations={
                        renderKnowledgeBaseCitations
                      }
                      canAnnotate={
                        shouldUseDrawer &&
                        activeImpression.status !== 'completed' &&
                        !activeImpression.reviewDiffDocument
                      }
                    >
                      <Box
                        className={classes.body}
                        gap={2}
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        <ImpressionEditor.Render
                          onDelete={onDelete}
                          lastEditedBySystem={SYSTEM_ACTION_PLAN_LAST_EDITED_BY}
                          analyticsSurface="annual_report"
                        />
                        <ESAnnotationPlugin />
                      </Box>
                      {shouldUseDrawer && (
                        <ESDrawer
                          patientId={props.patient.id}
                          executiveSummary={executiveSummary ?? undefined}
                        />
                      )}
                    </ImpressionEditor.Providers>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </CombinedRenderingTrackingProvider>
    </AttachmentArbiterProvider>
  );
};

const ExecutiveSummary = (props: ExecutiveSummaryProps) => {
  return (
    <ExecutiveSummaryProvider
      patient={props.patient}
      enabledSummaries={props.enabledSummaries}
      controlledCarePlanId={props.selectedReportId}
      onCarePlanIdChange={props.onReportChange}
      controlledImpressionTypeFilter={props.selectedSection}
      onImpressionTypeFilterChange={props.onSectionChange}
    >
      <_ExecutiveSummary {...props} />
    </ExecutiveSummaryProvider>
  );
};

const MemoizedExecutiveSummary = React.memo(ExecutiveSummary);

export default MemoizedExecutiveSummary;
